import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Bell, Check, Pencil, Trash2, X, Search } from "lucide-react";
import InstallInstructions from "@/components/InstallInstructions";
import { BELT_ORDER, beltLabel } from "@/lib/belts";

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff/60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  if (diff < 86400*2) return "Yesterday";
  if (diff < 86400*7) return `${Math.floor(diff/86400)} days ago`;
  return `${Math.floor(diff/86400/7)} week${Math.floor(diff/86400/7)>1?"s":""} ago`;
}

/**
 * Small search-and-pick control for the "Individual member" audience option.
 * Debounces filtering locally against the full students roster (~200 rows).
 */
function StudentPicker({ students, value, onChange }) {
  const [q, setQ] = useState("");
  const picked = students.find(s => s.student_id === value);
  const filtered = useMemo(() => {
    if (!q.trim()) return students.slice(0, 8);
    const l = q.toLowerCase();
    return students.filter(s => s.name?.toLowerCase().includes(l) || s.email?.toLowerCase().includes(l)).slice(0, 8);
  }, [q, students]);

  if (picked) {
    return (
      <div className="card p-3 mt-1 flex items-center gap-3" data-testid="ann-picked-student">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
             style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
          {picked.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[13px] truncate">{picked.name}</div>
          <div className="label-tiny truncate">{picked.email}</div>
        </div>
        <button type="button" onClick={() => onChange(null)} className="pill" data-testid="ann-picked-clear"
                style={{ background: "transparent", color: "var(--text-dim)", border: "1px solid var(--border)" }}>
          <X size={11} />
        </button>
      </div>
    );
  }
  return (
    <div className="mt-1">
      <div style={{ position: "relative" }}>
        <Search size={14} style={{ position: "absolute", left: 12, top: 12, color: "var(--text-dim)" }} />
        <input className="input" value={q} onChange={e => setQ(e.target.value)}
               placeholder="Search by name or email…" data-testid="ann-student-search"
               style={{ paddingLeft: 34 }} />
      </div>
      {filtered.length > 0 && (
        <div className="card mt-1 divide-y" style={{ borderColor: "var(--border)", maxHeight: 240, overflowY: "auto" }}>
          {filtered.map(s => (
            <button key={s.student_id} type="button" onClick={() => onChange(s.student_id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/5"
                    data-testid={`ann-student-option-${s.student_id}`}
                    style={{ borderColor: "var(--border)" }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
                   style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
                {s.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[13px] truncate">{s.name}</div>
                <div className="label-tiny truncate">{s.email || "no email"} · {s.belt}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Modal that re-uses the same form as create, but calls PATCH on save. */
function EditAnnouncementModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial.title, message: initial.message,
    audience: initial.audience, channel: initial.channel,
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/announcements/${initial.ann_id}`, form);
      onSaved(data);
    } finally { setSaving(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "grid", placeItems: "center", zIndex: 50, padding: 16 }}>
      <form onSubmit={submit} className="card w-full" style={{ maxWidth: 520 }} data-testid="ann-edit-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label" style={{ color: "var(--green)" }}>Edit broadcast</div>
              <div className="text-[20px] font-bold uppercase mt-1">Update announcement</div>
            </div>
            <button type="button" onClick={onClose} data-testid="ann-edit-close"><X size={18} /></button>
          </div>
          <label className="block label">Title
            <input required className="input mt-1" value={form.title}
                   onChange={e => setForm({ ...form, title: e.target.value })} data-testid="ann-edit-title" />
          </label>
          <label className="block label mt-4">Message
            <textarea required rows={4} className="input mt-1" value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })} data-testid="ann-edit-message" />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <label className="block label">Audience label
              <input className="input mt-1" value={form.audience}
                     onChange={e => setForm({ ...form, audience: e.target.value })} data-testid="ann-edit-audience"
                     placeholder="e.g. All members (94)" />
              <div className="label-tiny mt-1" style={{ color: "var(--text-dim)" }}>
                This is a display label. To change WHO receives the push, delete this and send a new broadcast.
              </div>
            </label>
            <label className="block label">Channel
              <select className="input mt-1" value={form.channel}
                      onChange={e => setForm({ ...form, channel: e.target.value })} data-testid="ann-edit-channel">
                <option>Push + In-app</option>
                <option>In-app only</option>
                <option>Email + Push</option>
                <option>Email + In-app</option>
              </select>
            </label>
          </div>
          <div className="label-tiny mt-3" style={{ color: "var(--text-dim)" }}>
            Note: editing does NOT re-send the push or email. It only updates the stored record so viewers see the corrected copy.
          </div>
          <button type="submit" disabled={saving} className="btn btn-primary w-full mt-5" data-testid="ann-edit-save">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BroadcastRow({ a, isLast, onEdit, onDelete }) {
  return (
    <div className={`flex flex-col md:flex-row md:items-start md:justify-between gap-2 py-4 ${isLast ? "" : "border-b"}`}
      style={{borderColor:"var(--border)"}} data-testid={`broadcast-${a.ann_id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold uppercase tracking-tight">{a.title}</div>
        <div className="label-tiny mt-1">{a.message.slice(0,90)}{a.message.length>90?"…":""}</div>
      </div>
      <div className="md:text-right flex-shrink-0 flex md:flex-col items-center md:items-end gap-2">
        <div className="label-tiny" style={{color:"var(--green)"}}>{timeAgo(a.created_at)}{a.updated_at ? " · edited" : ""}</div>
        <div className="label-tiny">{a.audience}{a.push_sent != null ? ` · ${a.push_sent} push` : ""}{a.email_queued ? ` · ${a.email_sent || 0}/${a.email_queued} email` : ""}</div>
        <div className="flex gap-1.5 md:mt-1">
          <button onClick={() => onEdit(a)} className="btn btn-outline" style={{ padding: "5px 8px" }} data-testid={`ann-edit-${a.ann_id}`} title="Edit">
            <Pencil size={12}/>
          </button>
          <button onClick={() => onDelete(a)} className="btn btn-outline"
                  style={{ padding: "5px 8px", borderColor: "rgba(255,80,80,0.4)", color: "rgb(255,120,120)" }}
                  data-testid={`ann-delete-${a.ann_id}`} title="Delete">
            <Trash2 size={12}/>
          </button>
        </div>
      </div>
    </div>
  );
}

function RecentBroadcastsList({ items, onEdit, onDelete }) {
  return (
    <div className="card p-5 md:p-6">
      <div className="label mb-4">Recent broadcasts</div>
      {items.length === 0 ? (
        <div className="label-tiny" style={{color:"var(--text-dim)"}}>No announcements yet.</div>
      ) : items.map((a, i) => (
        <BroadcastRow key={a.ann_id} a={a} isLast={i === items.length - 1} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </div>
  );
}

function PushPreviewCard({ academyName, form, stats, reachCount }) {
  return (
    <div className="card p-5 md:p-6">
      <div className="label">Preview · push notification</div>
      <div className="card p-4 mt-4">
        <div className="flex items-center gap-2.5 mb-3">
          <img src="/icons/icon-192.png" alt="RollOSS"
            style={{width:24, height:24, borderRadius:6, objectFit:"cover", flexShrink:0}}
            data-testid="push-app-icon"/>
          <div className="label-tiny truncate">RollOSS · {academyName} · now</div>
        </div>
        <div className="font-semibold text-[14px]">{form.title || "Your title appears here"}</div>
        <div className="text-[13px] mt-1" style={{color:"var(--text-dim)"}}>{form.message || "Your message appears here…"}</div>
      </div>
      <div className="divider my-5"/>
      <div className="label">Estimated reach</div>
      <div className="text-[28px] font-bold mt-1" style={{color: reachCount > 0 ? "var(--green)" : "var(--text-dim)"}} data-testid="reach-count">
        {reachCount} <span className="text-[14px] mono" style={{color:"var(--text-dim)"}}>
          {reachCount === 1 ? "member" : "members"}
        </span>
      </div>
      <div className="label-tiny mt-1">
        {stats.all === 0
          ? "Import your roster to start reaching members."
          : `${stats.active} active · ${stats.trial} trial`}
      </div>
    </div>
  );
}

function ComposeForm({
  form, setForm, audience, setAudience, beltFilter, setBeltFilter,
  students, pickedStudent, setPickedStudent, stats, beltCounts,
  scheduled, setScheduled, onSubmit,
}) {
  return (
    <form onSubmit={onSubmit} className="card p-5 md:p-6 lg:col-span-2">
      <div className="label" style={{color:"var(--green)"}}>New broadcast</div>
      <div className="text-[20px] md:text-[22px] font-bold uppercase mt-1 tracking-tight">Send an announcement</div>
      <div className="divider my-5"/>
      <label className="block label">Title
        <input required className="input mt-1" value={form.title}
          onChange={e=>setForm({...form, title:e.target.value})}
          placeholder="Seminar · Prof. [Name]" data-testid="ann-title"/>
      </label>
      <label className="block label mt-4">Message
        <textarea required className="input mt-1" rows={4} value={form.message}
          onChange={e=>setForm({...form, message:e.target.value})}
          placeholder="Saturday gi seminar — focus on guard retention. 14:00 on Mat A. Limited spots, reserve yours."
          data-testid="ann-message"/>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <label className="block label">Audience
          <select className="input mt-1" value={audience}
            onChange={e=>setAudience(e.target.value)} data-testid="ann-audience">
            <option value="all">All members ({stats.all})</option>
            <option value="active">Active only ({stats.active})</option>
            <option value="trial">Trial members ({stats.trial})</option>
            <option value="belt">By belt…</option>
            <option value="individual">Individual member…</option>
            <option value="coaches">Coaches</option>
          </select>
        </label>
        <label className="block label">Channel
          <select className="input mt-1" value={form.channel}
            onChange={e=>setForm({...form, channel:e.target.value})}>
            <option>Push + In-app</option><option>In-app only</option><option>Email + Push</option>
          </select>
        </label>
      </div>

      {audience === "belt" && (
        <label className="block label mt-4">Belt
          <select className="input mt-1" value={beltFilter}
                  onChange={e => setBeltFilter(e.target.value)} data-testid="ann-belt">
            {BELT_ORDER.map(b => (
              <option key={b} value={b}>{beltLabel(b)} ({beltCounts[b] || 0})</option>
            ))}
          </select>
        </label>
      )}

      {audience === "individual" && (
        <div className="mt-4">
          <div className="label">Recipient</div>
          <StudentPicker students={students} value={pickedStudent} onChange={setPickedStudent} />
        </div>
      )}

      <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3 mt-5">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={scheduled} onChange={e=>setScheduled(e.target.checked)} data-testid="ann-schedule"/>
          <span style={{color:"var(--text-dim)"}}>Schedule for later</span>
        </label>
        <div className="flex gap-2">
          <button type="button" className="btn btn-outline" data-testid="ann-save-draft">Save draft</button>
          <button type="submit" className="btn btn-primary" data-testid="ann-send">Send now <ArrowRight size={13}/></button>
        </div>
      </div>
    </form>
  );
}

/** Derived audience stats (reach count + display label) shared by the compose form and preview. */
function useAudienceDerivedState({ audience, pickedStudent, beltFilter, students, stats }) {
  const beltCounts = useMemo(() => {
    const m = {};
    for (const s of students) m[s.belt] = (m[s.belt] || 0) + 1;
    return m;
  }, [students]);

  const reachCount = useMemo(() => {
    if (audience === "individual") return pickedStudent ? 1 : 0;
    if (audience === "belt") return beltCounts[beltFilter] || 0;
    if (audience === "active") return stats.active;
    if (audience === "trial") return stats.trial;
    if (audience === "coaches") return 0;
    return stats.all;
  }, [audience, pickedStudent, beltFilter, beltCounts, stats]);

  const audienceLabel = useMemo(() => {
    if (audience === "individual") {
      const s = students.find(x => x.student_id === pickedStudent);
      return s ? `@${s.name}` : "Individual (unpicked)";
    }
    if (audience === "belt") return `Belt: ${beltLabel(beltFilter)} (${beltCounts[beltFilter] || 0})`;
    if (audience === "active") return `Active only (${stats.active})`;
    if (audience === "trial") return `Trial members (${stats.trial})`;
    if (audience === "coaches") return "Coaches";
    return `All members (${stats.all})`;
  }, [audience, pickedStudent, beltFilter, beltCounts, students, stats]);

  return { beltCounts, reachCount, audienceLabel };
}

export default function Announcements() {
  const { user } = useAuth();
  const academyName = user?.academy_name || "Your Academy";
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({ all: 0, active: 0, trial: 0 });
  const [audience, setAudience] = useState("all");
  const [beltFilter, setBeltFilter] = useState("blue");
  const [pickedStudent, setPickedStudent] = useState(null);
  const [form, setForm] = useState({ title: "", message: "", channel: "Push + In-app" });
  const [scheduled, setScheduled] = useState(false);
  const [testStatus, setTestStatus] = useState("");
  const [editing, setEditing] = useState(null);

  const loadAnns = () => api.get("/announcements").then(r => setItems(r.data));

  useEffect(() => {
    api.get("/students").then(r => {
      const s = r.data || [];
      setStudents(s);
      setStats({
        all: s.length,
        active: s.filter(x => x.status === "active").length,
        trial: s.filter(x => x.status === "trial").length,
      });
    });
    loadAnns();
  }, []);

  const { beltCounts, reachCount, audienceLabel } = useAudienceDerivedState({ audience, pickedStudent, beltFilter, students, stats });

  const sendTestPush = async () => {
    setTestStatus("sending");
    try {
      const { data } = await api.post("/push/test");
      setTestStatus(`sent:${data.sent}`);
      setTimeout(() => setTestStatus(""), 4000);
    } catch (e) { setTestStatus("error"); setTimeout(() => setTestStatus(""), 4000); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (audience === "individual" && !pickedStudent) {
      alert("Please pick a student for the Individual audience.");
      return;
    }
    const body = {
      title: form.title,
      message: form.message,
      audience: audienceLabel,
      channel: form.channel,
    };
    if (audience === "individual") body.student_id = pickedStudent;
    if (audience === "belt") body.belt = beltFilter;
    try {
      await api.post("/announcements", body);
      setForm({ ...form, title: "", message: "" });
      setPickedStudent(null);
      loadAnns();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not send announcement");
    }
  };

  const removeBroadcast = async (a) => {
    if (!confirm(`Delete "${a.title}"? Students who already received the push will still see it on their phone.`)) return;
    await api.delete(`/announcements/${a.ann_id}`);
    loadAnns();
  };

  const onEdited = (updated) => {
    setEditing(null);
    setItems(prev => prev.map(x => x.ann_id === updated.ann_id ? { ...x, ...updated } : x));
  };

  return (
    <div data-testid="page-announcements">
      <PageHeader section="Engage" title="Announcements"
        right={
          <button onClick={sendTestPush} className="btn btn-outline" data-testid="btn-test-push">
            {testStatus === "sending" ? "Sending…"
              : testStatus.startsWith("sent") ? <><Check size={13}/>Push sent to you ({testStatus.split(":")[1]})</>
              : testStatus === "error" ? "Push failed — enable on your phone"
              : <><Bell size={13}/>Send test push to me</>}
          </button>
        }
      />

      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ComposeForm
          form={form} setForm={setForm}
          audience={audience} setAudience={setAudience}
          beltFilter={beltFilter} setBeltFilter={setBeltFilter}
          students={students} pickedStudent={pickedStudent} setPickedStudent={setPickedStudent}
          stats={stats} beltCounts={beltCounts}
          scheduled={scheduled} setScheduled={setScheduled}
          onSubmit={submit}
        />
        <PushPreviewCard academyName={academyName} form={form} stats={stats} reachCount={reachCount} />
      </div>

      <div className="p-4 md:p-8 pt-0">
        <InstallInstructions variant="card" />
      </div>

      <div className="p-4 md:p-8 pt-0">
        <RecentBroadcastsList items={items} onEdit={setEditing} onDelete={removeBroadcast} />
      </div>

      {editing && (
        <EditAnnouncementModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={onEdited}
        />
      )}
    </div>
  );
}
